import { h } from "vue";

import { useNotificationsStore } from "@/entities";
import { deleteAuthToken, isAuthRoute } from "@/pages";
import {
    AUTHENTICATION_ROUTE_NAMES,
    TARIFF_ROUTE_NAMES,
    mapErrorFromServer,
} from "@/shared";

// Запасной текст отказа: сервер присылает причину в error/message, но если
// её нет, в шапке уведомления оказывалось системное «Request failed with
// status code 403»
const ACCESS_DENIED_MESSAGE = "Недостаточно прав для этого действия";

// 403 в этом API — код по умолчанию для любого делового отказа: «товар не
// найден», «заказ уже закрыт» и ещё три с половиной сотни мест. Показывать
// на них общий отказ по правам нельзя — это и неверно по смыслу, и даёт
// вторую всплывашку поверх той, что уже показал вызывающий код.
// Показываем только те отказы, что помечены признаком: проверка прав шлёт
// NO_ACCESS, проверка действующей подписки — TARIFF_EXPIRED.
const MARKED_REFUSALS = ["NO_ACCESS", "TARIFF_EXPIRED"];

const isAccessDenied = (error) =>
    MARKED_REFUSALS.includes(error?.response?.data?.status);

// Упор в лимит тарифа (заказы, сервисные центры, сотрудники) сервер помечает
// upgrade_required: к тексту отказа добавляется переход к выбору тарифа
const isTariffLimit = (error) =>
    error?.response?.data?.upgrade_required === true;

// router инжектится из app-слоя (App.vue), чтобы processes не импортировал
// app напрямую (нарушение FSD-границ).
export const createResponseRejectedHandler = (router) => (error) => {
    const status = error?.response?.status;

    // Фоновые запросы кабинета (их человек не инициировал) помечены
    // silentRefusal: отказ по правам на них — норма, а не событие для
    // всплывашки. Иначе сотрудник без раздела ловил её на каждом входе
    if (status === 403 && error?.config?.silentRefusal) {
        return;
    }

    // Вызывающий код ошибку с этим признаком не показывает (см. setErrorNotify),
    // иначе поверх уведомления с переходом легла бы вторая, без него
    if (status === 403 && isTariffLimit(error)) {
        const limitError = mapErrorFromServer(error);

        useNotificationsStore().setNotify({
            type: "error",
            title: limitError.data?.message ?? "Лимит тарифа исчерпан",
            text: h(
                "a",
                {
                    href: "#",
                    onClick: (event) => {
                        event.preventDefault();
                        router.push({ name: TARIFF_ROUTE_NAMES.PAY });
                    },
                },
                "Перейти к тарифам"
            ),
        });

        return;
    }

    // Отказ по правам обрабатывался только на 401: на 403 экран молчал —
    // человек жал пункт меню или кнопку и не понимал, произошло ли хоть что-то
    if (status === 403 && isAccessDenied(error)) {
        const accessError = mapErrorFromServer(error);

        accessError.data = {
            ...accessError.data,
            message: accessError.data?.message ?? ACCESS_DENIED_MESSAGE,
        };

        useNotificationsStore().setErrorNotify(accessError);

        return;
    }

    if (status !== 401) {
        return;
    }

    deleteAuthToken();

    const currentRoute = router.currentRoute.value;

    // Раньше здесь был location.reload() — он уничтожал весь Pinia-стейт,
    // включая несохранённую аренду. SPA-переход на логин сохраняет данные в
    // памяти и черновик; returnUrl позволяет вернуть пользователя обратно.
    if (!isAuthRoute(currentRoute.name)) {
        router.push({
            name: AUTHENTICATION_ROUTE_NAMES.LOGIN,
            query: { returnUrl: currentRoute.fullPath },
        });
    }
};
